import { useQuery } from '@tanstack/react-query';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { me, meKeys } from '../api/auth';
import { ApiClientError } from '../api/client';
import { useMustRenameStore } from './mustRenameStore';

/**
 * Task 15 review fix (IMPORTANT-1): the Decision #10 rename gate must cover EVERY authenticated
 * route, not just the tabbed `<AppShell>` ones. `/wardrobe/:companionId`, `/world/battle/:battleId`,
 * and `/world/minigames/:gameCode` are router SIBLINGS of `<AppShell>` (deliberate full-screen
 * takeovers, outside the shell — see routes.tsx's own comments on each) — a redirect that lives
 * only inside `AppShell` never even mounts for a direct deep link, a restored back-history entry,
 * or a typed URL straight into one of those siblings, so the "non-dismissible" claim screen
 * (SCREENS.md "/claim-handle") was trivially bypassable for exactly that subset of routes.
 *
 * This component is the single router-level choke point instead: routes.tsx wraps `AppShell` AND
 * its full-screen siblings in ONE shared parent route using this as that parent's `element` —
 * nothing below it in the tree can mount while `mustRename` is true, regardless of which specific
 * route matched. A per-screen patch (adding the same check to `WardrobeScreen`/`BattleScreen`/
 * `MinigameScreen` individually) was rejected on purpose — a fourth full-screen sibling added
 * later would silently reopen the same hole; a single router-level wrapper can't be forgotten.
 *
 * Primes the mustRename latch itself (the same `useQuery(['me'])` `AppShell` used to run before
 * this fix moved it here) so a fresh page load / direct link still catches a RETURNING session,
 * not just a fresh login (which sets the latch directly from `LoginScreen`/`RegisterScreen`'s own
 * mutation response, before this component ever mounts). Declarative, not a one-shot imperative
 * `navigate()` — `mustRename` is re-read on every render, so browser back-navigation into any
 * wrapped route re-asserts the redirect automatically (SCREENS.md's "non-dismissible" requirement)
 * with no extra plumbing.
 *
 * Never wraps `/login`, `/register`, or `/claim-handle` itself (see routes.tsx) — redirecting an
 * unauthenticated visitor, or the claim screen's own render, to itself would be nonsensical.
 *
 * Signed-out visitors (onboarding 2026-09-23): a 401/403 from this `/me` read means there is no
 * session, so the visitor goes to /login carrying the route they wanted (`state.from`). Before
 * this, the 401 was swallowed and a first-time visitor to projectpets.app sat on an endless
 * Habitat skeleton — `client.ts`'s global handler only fires once a session has existed in the
 * tab, and `/me` is exempt from it, so nothing else could ever redirect them. Any other failure
 * (5xx, offline) renders the Outlet as before: only a missing session is a reason to leave.
 */
export function RequireHandleClaimed() {
  const { error, isPending } = useQuery({ queryKey: meKeys.detail, queryFn: me, retry: false });
  const mustRename = useMustRenameStore((s) => s.mustRename);
  const { pathname, search } = useLocation();

  if (error instanceof ApiClientError && (error.status === 401 || error.status === 403)) {
    return <Navigate to="/login" replace state={{ from: pathname + search }} />;
  }
  // QA 2026-09-23 onboard F5: nothing below this gate mounts until the session question is
  // answered. The Outlet used to render while `/me` was in flight, so a signed-out visit fired
  // the shell's six authenticated queries into a wall of 403s before the redirect above ran.
  // A fresh login seeds this cache synchronously (LoginScreen/RegisterScreen's onSuccess), so
  // only a cold load or a genuinely signed-out visit ever waits here, and only for `/me`.
  if (mustRename) return <Navigate to="/claim-handle" replace />;
  if (isPending) return null;
  return <Outlet />;
}
